import { Icon, Link, Spinner, Stack } from '@fluentui/react';
import { makeAutoObservable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import {
	AccessLevel,
	EntityAPI,
	EntityType,
	IEntityTypeUserInfo,
	IGenericEntity,
	ITaskRequestFilterField,
} from '../../API';
import { IUserConfigPackage } from '../../API';
import EntitySelectorDialogCtrl from './EntitySelectorDialogCtrl';

export enum EntitySelectorMode {
	//Allows you to pick an item, and displays the current selection.
	Picker,

	//Allows you to pick an item, without displaying the current selection (you only get a selection callback)
	PickerWithoutSelection,

	//Just render the selection dialog
	DialogOnly,
}

export interface IEntitySelectorProps {
	state: EntitySelectorCtrlState;
}

export class EntitySelectorCtrlState {
	public dialogVisible: boolean = false;
	public loading: boolean = false;
	public loaded: boolean = false; //Indicates that loading is complete, even if the item wasn't found.
	public items: IGenericEntity[] = []; //Selected items

	//Indicates that we shouldn't show a selected item and edit option - we should just be rendering a dialog
	public mode: EntitySelectorMode;
	public multi: boolean = false;

	public emptyLabel: string = 'Select...';
	public editLabel?: string = 'edit';

	public selectedItemIds: string[] = [];
	public onSelectionChangeCallback: (items: IGenericEntity[]) => void = (
		_,
	) => {};

	//Filters that apply to the selector dialog
	public permaFilters: ITaskRequestFilterField[] = [];

	public userConfig: IUserConfigPackage;
	public accessLevel: AccessLevel; //Dialog could be in Read or Edit mode
	public entityType: string;
	public typeConfig?: IEntityTypeUserInfo;

	constructor(
		entityType: string | EntityType,
		userConfig: IUserConfigPackage,
		mode: EntitySelectorMode = EntitySelectorMode.Picker,
	) {
		this.userConfig = userConfig;
		this.mode = mode;
		this.accessLevel = AccessLevel.Manage;
		this.typeConfig = this.userConfig.entityTypes.getConfig(entityType);
		this.entityType = this.userConfig.entityTypes.getTypeId(entityType);

		makeAutoObservable(this);
	}

	public onSelectionChange(callback: (items: IGenericEntity[]) => void) {
		this.onSelectionChangeCallback = callback;
	}

	public setSelectedItemIds(ids: string | string[]) {
		if (Array.isArray(ids)) {
			this.selectedItemIds = ids;
		} else if (ids?.length) {
			this.selectedItemIds = [ids as string];
		} else {
			this.selectedItemIds = [];
		}

		//If the control is already loaded, load it again.
		if (this.isLoaded) {
			this.ensureLoaded();
		}
	}

	public isLoaded: boolean = false;
	public async ensureLoaded(): Promise<void> {

		let accessToken = await this.userConfig.getToken();
		let api = EntityAPI.fromEntityTypeId(this.entityType, accessToken);
		if (this.selectedItemIds.length > 0) {
			let items = await api.items(this.selectedItemIds, null);
			this.setSelection(items);
		} else {
			if (this.items.length > 0) {
				this.setSelection([]);
			}
		}

		this.isLoaded = true;
	}

	public showHide(show: boolean) {
		this.dialogVisible = show;
	}

	public showIndividualEntityDialog(item: IGenericEntity) {
		
	}

	public setSelection(items: IGenericEntity[]) {
		let selectedItems =
			this.mode === EntitySelectorMode.PickerWithoutSelection ? [] : items;
		this.items = selectedItems;
		if (this.dialogVisible) {
			this.dialogVisible = false;
		}
	}

	public addToSelection(items: IGenericEntity[]) {
		if (this.multi) {
			this.setSelection(this.items.concat(items));
		} else {
			this.setSelection(this.items[0] ? [this.items[0]] : []);
		}
		this.onSelectionChangeCallback(this.items);
	}

	public removeSelection(item: IGenericEntity) {
		let selectedItems = this.items.filter((i) => i.Id !== item.Id);
		this.setSelection(selectedItems);
		this.onSelectionChangeCallback(this.items);
	}
}

class EntitySelectorCtrl extends React.Component<IEntitySelectorProps, {}> {
	componentDidMount(): void {
		this.props.state.ensureLoaded();
	}

	render() {
		return (
			<>
				{this.props.state.loading ? <Spinner /> : <></>}

				{this.props.state.mode === EntitySelectorMode.DialogOnly ? (
					<></>
				) : (
					this.getSelectedItemCtrl()
				)}

				<EntitySelectorDialogCtrl
					permaFilters={this.props.state.permaFilters}
					visible={
						this.props.state.mode === EntitySelectorMode.DialogOnly ||
						this.props.state.dialogVisible
					}
					dialogTitle={'Select ' + this.props.state.typeConfig?.singularName || ''}
					entityType={this.props.state.entityType}
					userConfig={this.props.state.userConfig}
					multi={!!this.props.state.multi}
					onSelect={(items: IGenericEntity[]) => {
						this.props.state.setSelection(items);
						this.props.state.onSelectionChangeCallback(items);
					}}
					onCancel={() => {
						this.props.state.showHide(false);
					}}
				>
					{this.props.children}
				</EntitySelectorDialogCtrl>
			</>
		);
	}

	private getSelectedItemCtrl() {
		if (this.props.state.loading) {
			return <></>;
		}

		let editText = this.props.state.editLabel || 'edit';

		if (this.props.state.items.length === 0) {
			editText =
				this.props.state.emptyLabel ||
				'Select ' +
					(this.props.state.typeConfig?.singularName || '').toLocaleLowerCase();
		}

		let marginLeft = this.props.state.items.length ? 15 : 0;

		return (
			<Stack horizontal>
				<Stack horizontal>
					{this.props.state.items.map((item) => this.getSelectedItem(item))}
				</Stack>

				<span
					onClick={() => {
						this.props.state.showHide(true);
					}}
					style={{
						textDecoration: 'underline',
						cursor: 'pointer',
						marginLeft: marginLeft,
					}}
				>
					{editText} <Icon style={{ marginLeft: 5 }} iconName="Edit"></Icon>
				</span>
			</Stack>
		);
	}

	getSelectedItem(item: IGenericEntity): React.ReactElement {
		return (
			<Stack horizontal style={{ marginRight: 10 }}>
				<Link
					style={{ cursor: 'pointer', marginRight: 4 }}
					title="Click to view"
					onClick={() => {
						this.props.state.showIndividualEntityDialog(item);
					}}
				>
					{item.DisplayTitle}
				</Link>

				<Icon
					iconName="Delete"
					title="Click to remove"
					className="clickToDelete"
					onClick={() => {
						this.props.state.removeSelection(item);
					}}
				/>
			</Stack>
		);
	}
}

export default observer(EntitySelectorCtrl);
